import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Clock, Info, Palmtree, Save, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import SignaturePad from '../components/SignaturePad';
import PanelLimitowNadgodzin from '../components/PanelLimitowNadgodzin';
import nadgodzinyService from '../services/nadgodzinyService';
import { formatSignerNameUnderPad } from '../utils/userDisplayName';
import {
  dataDoYmd,
  formatGodziny,
  minutDoRozpoczecia,
  obliczLiczbeGodzin
} from '../utils/nadgodziny';

const MAX_GODZIN = 13;

const fieldInputClass =
  'w-full bg-transparent text-sm font-semibold text-ink placeholder:text-ink-faint placeholder:font-normal focus:outline-none';

const SectionBadge = ({ n }) => (
  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-surface border border-black/[0.06] text-xs font-bold text-ink">
    {n}
  </span>
);

const FieldBox = ({ label, children, className = '' }) => (
  <div className={`rounded-xl border border-gray-200 bg-white px-4 py-2.5 ${className}`}>
    <label className="block text-[11px] font-medium text-ink-faint mb-0.5">{label}</label>
    {children}
  </div>
);

const dzisiajYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const pustyFormularz = (user) => ({
  pracownikImie: user?.firstName || '',
  pracownikNazwisko: user?.lastName || '',
  stanowisko: user?.position || '',
  dzial: user?.department || '',
  miejscowosc: '',
  planowanaData: dzisiajYmd(),
  godzinaOd: '',
  godzinaDo: '',
  projekt: '',
  zakresPrac: '',
  potrzebaBiznesowa: '',
  wnioskujeOCzasWolny: false,
  podpisPracownika: '',
  przełożony: ''
});

const WniosekNadgodziny = () => {
  const { id: editId } = useParams();
  const isEditMode = !!editId;
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const przelozonyZainicjowany = useRef(false);
  const wniosekZaladowany = useRef(false);
  const [formData, setFormData] = useState(() => pustyFormularz(user));

  const set = (pole) => (e) => setFormData((prev) => ({ ...prev, [pole]: e.target.value }));

  const {
    data: kontekst,
    isLoading: kontekstLoading,
    isError: kontekstError,
    refetch: refetchKontekst
  } = useQuery('nadgodziny-kontekst-zlozenia', nadgodzinyService.getKontekstZlozenia, {
    staleTime: 2 * 60 * 1000,
    retry: 1,
    enabled: !isEditMode
  });

  const { data: istniejacyData, isLoading: wniosekLoading, isError: wniosekError } = useQuery(
    ['wniosek-nadgodziny', editId],
    () => nadgodzinyService.getWniosek(editId),
    { enabled: isEditMode, retry: 1 }
  );
  const istniejacy = istniejacyData?.wniosek;

  useEffect(() => {
    if (!kontekst || przelozonyZainicjowany.current) return;
    przelozonyZainicjowany.current = true;
    setFormData((prev) => ({ ...prev, przełożony: kontekst.domyslnyPrzełożonyId || '' }));
  }, [kontekst]);

  useEffect(() => {
    if (!istniejacy?.oczekujeNaPoprawke || wniosekZaladowany.current) return;
    wniosekZaladowany.current = true;
    setFormData({
      pracownikImie: istniejacy.pracownikImie || '',
      pracownikNazwisko: istniejacy.pracownikNazwisko || '',
      stanowisko: istniejacy.stanowisko || '',
      dzial: istniejacy.dzial || '',
      miejscowosc: istniejacy.miejscowosc || '',
      planowanaData: dataDoYmd(istniejacy.planowanaData),
      godzinaOd: istniejacy.godzinaOd || '',
      godzinaDo: istniejacy.godzinaDo || '',
      projekt: istniejacy.projekt || '',
      zakresPrac: istniejacy.zakresPrac || '',
      potrzebaBiznesowa: istniejacy.potrzebaBiznesowa || '',
      wnioskujeOCzasWolny: istniejacy.wnioskujeOCzasWolny === true,
      podpisPracownika: istniejacy.podpisPracownika || '',
      przełożony: istniejacy.przełożony?._id || istniejacy.przełożony || ''
    });
  }, [istniejacy]);

  const liczbaGodzin = obliczLiczbeGodzin(formData.godzinaOd, formData.godzinaDo);
  const godzinyKompletne = !!(formData.godzinaOd && formData.godzinaDo && formData.planowanaData);
  const { data: limityData, isFetching: limityLoading } = useQuery(
    ['nadgodziny-limity', formData.planowanaData, formData.godzinaOd, formData.godzinaDo, editId || ''],
    () => nadgodzinyService.getLimity({
      planowanaData: formData.planowanaData,
      godzinaOd: formData.godzinaOd,
      godzinaDo: formData.godzinaDo,
      ...(isEditMode ? { pomin: editId } : {})
    }),
    { enabled: !!user && !!formData.planowanaData, keepPreviousData: true, retry: 1 }
  );
  const stanLimitow = limityData?.stan;
  const limitPrzekroczony = godzinyKompletne && !!stanLimitow?.rok?.przekroczony;
  const przezPolnoc = formData.godzinaOd && formData.godzinaDo && formData.godzinaDo <= formData.godzinaOd;
  const doRozpoczecia = minutDoRozpoczecia(formData.planowanaData, formData.godzinaOd);

  const zapiszMutation = useMutation(
    (dane) => (isEditMode ? nadgodzinyService.update(editId, dane) : nadgodzinyService.create(dane)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('nadgodziny-moje');
        if (isEditMode) queryClient.invalidateQueries(['wniosek-nadgodziny', editId]);
        toast.success(isEditMode ? 'Poprawiony wniosek został przesłany do przełożonego' : 'Wniosek został przesłany do przełożonego');
        navigate('/nadgodziny?tab=moje');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Wystąpił błąd podczas składania wniosku');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (zapiszMutation.isLoading) return;

    if (!formData.planowanaData || !formData.godzinaOd || !formData.godzinaDo) {
      toast.error('Podaj planowaną datę i godziny pracy');
      return;
    }
    if (doRozpoczecia != null && doRozpoczecia <= 0) {
      toast.error('Wniosek należy złożyć przed rozpoczęciem pracy — wskaż przyszły termin');
      return;
    }
    if (liczbaGodzin > MAX_GODZIN) {
      toast.error(`Nadgodziny nie mogą przekraczać ${MAX_GODZIN} godzin (11 godzin odpoczynku dobowego)`);
      return;
    }
    if (limitPrzekroczony) {
      toast.error('Ten wniosek przekracza roczny limit nadgodzin — skontaktuj się z przełożonym lub HR');
      return;
    }
    if (!formData.zakresPrac.trim() || !formData.potrzebaBiznesowa.trim()) {
      toast.error('Uzupełnij zakres prac i potrzebę biznesową');
      return;
    }
    if (!formData.podpisPracownika) {
      toast.error('Podpisz wniosek');
      return;
    }
    if (!isEditMode && !formData.przełożony) {
      toast.error('Brak przełożonego, do którego trafi wniosek');
      return;
    }

    const { przełożony, ...reszta } = formData;
    zapiszMutation.mutate(isEditMode ? reszta : { ...reszta, przełożony });
  };

  if (isEditMode && wniosekLoading) {
    return <div className="card p-12"><LoadingSpinner /></div>;
  }

  if (isEditMode && (wniosekError || (istniejacy && !istniejacy.oczekujeNaPoprawke))) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-orange-500 mb-4" />
        <h2 className="text-lg font-semibold text-ink mb-2">Nie można edytować tego wniosku</h2>
        <p className="text-ink-soft mb-4">
          {wniosekError ? 'Nie udało się wczytać wniosku.' : 'Ten wniosek nie oczekuje na poprawę.'}
        </p>
        <Link to="/nadgodziny?tab=moje" className="text-primary-600 hover:underline">Wróć do moich wniosków</Link>
      </div>
    );
  }

  const brakAkceptanta =
    !isEditMode &&
    kontekst &&
    (!kontekst.maPrzełożonego || (kontekst.glownyNaUrlopie && (kontekst.listaZarzadu || []).length === 0));

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-1.5 text-xs text-ink-faint">
        <Link to="/nadgodziny?tab=moje" className="hover:text-ink">Nadgodziny</Link>
        <span>/</span>
        <span className="text-ink font-medium">{isEditMode ? 'Poprawa wniosku' : 'Nowy wniosek'}</span>
      </div>

      <div className="card px-6 py-5">
        <div className="flex items-center gap-3">
          <Link to="/nadgodziny" className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0" title="Powrót">
            <ArrowLeft className="h-5 w-5 text-ink-soft" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-ink">
              {isEditMode ? 'Popraw wniosek o nadgodziny' : 'Przedwstępny wniosek o zgodę na pracę w godzinach nadliczbowych'}
            </h1>
            <p className="text-sm text-ink-faint mt-0.5">
              Wymagana akceptacja bezpośredniego przełożonego przed rozpoczęciem pracy. Zarząd otrzymuje informację o wniosku.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {isEditMode && istniejacy?.aktywnyKomentarzZwrotu?.komentarz && (
          <div className="card px-5 py-4 bg-orange-50 border-orange-200">
            <p className="text-sm font-medium text-orange-900 flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4" />
              Uwagi przełożonego ({istniejacy.aktywnyKomentarzZwrotu.zwrocilImieNazwisko})
            </p>
            <p className="text-sm text-orange-900 mt-2 whitespace-pre-wrap">{istniejacy.aktywnyKomentarzZwrotu.komentarz}</p>
          </div>
        )}

        {/* 1. Dane pracownika */}
        <div className="card px-6 py-5">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={1} />
            <h3 className="text-base font-semibold text-ink flex items-center gap-2">
              <User className="h-4 w-4 text-primary-600" />
              Dane pracownika
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldBox label="Imię *">
              <input type="text" required value={formData.pracownikImie} onChange={set('pracownikImie')} className={fieldInputClass} />
            </FieldBox>
            <FieldBox label="Nazwisko *">
              <input type="text" required value={formData.pracownikNazwisko} onChange={set('pracownikNazwisko')} className={fieldInputClass} />
            </FieldBox>
            <FieldBox label="Stanowisko">
              <input type="text" value={formData.stanowisko} onChange={set('stanowisko')} className={fieldInputClass} placeholder="np. Specjalista ds. administracji" />
            </FieldBox>
            <FieldBox label="Dział">
              <input type="text" value={formData.dzial} onChange={set('dzial')} className={fieldInputClass} placeholder="np. ADMINISTRACJA" />
            </FieldBox>
            <FieldBox label="Miejscowość">
              <input type="text" value={formData.miejscowosc} onChange={set('miejscowosc')} className={fieldInputClass} placeholder="np. Warszawa" />
            </FieldBox>
            <FieldBox label="Data złożenia wniosku" className="!bg-surface">
              <input type="text" readOnly value={new Date().toLocaleDateString('pl-PL')} className={fieldInputClass} />
            </FieldBox>
          </div>
        </div>

        {/* 2. Szczegóły dotyczące nadgodzin */}
        <div className="card px-6 py-5">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={2} />
            <h3 className="text-base font-semibold text-ink flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary-600" />
              Szczegóły dotyczące nadgodzin
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FieldBox label="Planowana data *">
              <input type="date" required min={dzisiajYmd()} value={formData.planowanaData} onChange={set('planowanaData')} className={fieldInputClass} />
            </FieldBox>
            <FieldBox label="Od godz. *">
              <input type="time" required value={formData.godzinaOd} onChange={set('godzinaOd')} className={fieldInputClass} />
            </FieldBox>
            <FieldBox label="Do godz. *">
              <input type="time" required value={formData.godzinaDo} onChange={set('godzinaDo')} className={fieldInputClass} />
            </FieldBox>
            <FieldBox
              label="Szacowana liczba godzin"
              className={liczbaGodzin > MAX_GODZIN ? '!bg-red-50 !border-red-300' : '!bg-primary-50/70 !border-primary-200'}
            >
              <input type="text" readOnly value={liczbaGodzin != null ? formatGodziny(liczbaGodzin) : ''} placeholder="Obliczana automatycznie" className={fieldInputClass} />
            </FieldBox>
            <FieldBox label="Nazwa / nr projektu" className="sm:col-span-2 lg:col-span-4">
              <input type="text" maxLength={200} value={formData.projekt} onChange={set('projekt')} className={fieldInputClass} placeholder="np. PRJ-2026/041 Wdrożenie systemu" />
            </FieldBox>
          </div>
          {przezPolnoc && (
            <p className="mt-2 text-xs text-ink-faint">Godzina zakończenia jest wcześniejsza niż rozpoczęcia — praca kończy się następnego dnia.</p>
          )}
          {liczbaGodzin > MAX_GODZIN && (
            <p className="mt-2 text-xs text-red-700">
              Maksymalnie {MAX_GODZIN} godzin — pracownikowi przysługuje 11 godzin nieprzerwanego odpoczynku dobowego.
            </p>
          )}
          {doRozpoczecia != null && doRozpoczecia <= 0 && (
            <p className="mt-2 text-xs text-red-700 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              Ten termin już się rozpoczął — wniosek musi być złożony przed rozpoczęciem nadgodzin.
            </p>
          )}
          {doRozpoczecia != null && doRozpoczecia > 0 && doRozpoczecia < 120 && (
            <p className="mt-2 text-xs text-amber-700 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              Do rozpoczęcia zostało {doRozpoczecia} min — przełożony może nie zdążyć z decyzją. Nie zaczynaj pracy bez zgody.
            </p>
          )}
          <div className="mt-3">
            <PanelLimitowNadgodzin stan={stanLimitow} tryb="skladanie" ladowanie={limityLoading} />
          </div>
        </div>

        {/* 3. Opis zadania i uzasadnienie */}
        <div className="card px-6 py-5">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={3} />
            <h3 className="text-base font-semibold text-ink">Opis zadania i uzasadnienie</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label htmlFor="zakres-prac" className="block text-sm font-medium text-ink mb-1.5">
                Zakres prac * <span className="text-ink-faint font-normal">(co dokładnie zostanie zrobione?)</span>
              </label>
              <textarea id="zakres-prac" rows={3} maxLength={2000} required value={formData.zakresPrac} onChange={set('zakresPrac')} className="input resize-y min-h-[5rem]" />
            </div>
            <div>
              <label htmlFor="potrzeba-biznesowa" className="block text-sm font-medium text-ink mb-1.5">
                Potrzeba biznesowa * <span className="text-ink-faint font-normal">(dlaczego poza normalnymi godzinami?)</span>
              </label>
              <textarea id="potrzeba-biznesowa" rows={3} maxLength={2000} required value={formData.potrzebaBiznesowa} onChange={set('potrzebaBiznesowa')} className="input resize-y min-h-[5rem]" />
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-surface px-4 py-3">
              <input
                type="checkbox"
                checked={formData.wnioskujeOCzasWolny}
                onChange={(e) => setFormData((prev) => ({ ...prev, wnioskujeOCzasWolny: e.target.checked }))}
                className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-ink">
                <span className="font-medium">Wnioskuję o udzielenie czasu wolnego w zamian za nadgodziny (1:1)</span>
                <span className="block text-ink-faint mt-0.5">
                  Zgodnie z art. 151² § 1 KP czas wolny w wymiarze 1:1 przysługuje na pisemny wniosek pracownika. O sposobie
                  rozliczenia ostatecznie decyduje przełożony.
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* 4. Przekazanie do decyzji i podpis */}
        <div className="card px-6 py-5">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={4} />
            <h3 className="text-base font-semibold text-ink">Przekaż do decyzji przełożonego</h3>
          </div>

          {isEditMode && istniejacy?.przełożony && (
            <div className="mb-4 px-4 py-3 bg-surface border border-gray-200 rounded-xl text-sm">
              Wniosek wróci do: <strong>{istniejacy.przełożony.firstName} {istniejacy.przełożony.lastName}</strong>
            </div>
          )}

          {!isEditMode && kontekstLoading && (
            <div className="flex items-center gap-3 px-4 py-6 bg-surface border border-gray-200 rounded-xl">
              <LoadingSpinner size="sm" />
              <span className="text-sm text-ink-soft">Sprawdzanie przełożonego i dostępności…</span>
            </div>
          )}

          {!isEditMode && kontekstError && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
              Nie udało się ustalić przełożonego.{' '}
              <button type="button" onClick={() => refetchKontekst()} className="font-medium underline">Odśwież</button>
            </div>
          )}

          {!isEditMode && kontekst && !kontekst.maPrzełożonego && (
            <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-amber-800 font-medium flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4" />
                Brak przełożonego w profilu
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Administrator musi ustawić pole „Przełożony główny” przy Twoim koncie, zanim złożysz wniosek.
              </p>
            </div>
          )}

          {!isEditMode && kontekst?.maPrzełożonego && !kontekst.glownyNaUrlopie && (
            <div className="mb-4 px-4 py-3 bg-surface border border-gray-200 rounded-xl">
              <p className="text-xs text-ink-faint">Bezpośredni przełożony</p>
              <p className="font-medium text-ink">
                {kontekst.glownyPrzełożony?.firstName} {kontekst.glownyPrzełożony?.lastName}
              </p>
              {kontekst.glownyPrzełożony?.email && <p className="text-sm text-ink-faint">{kontekst.glownyPrzełożony.email}</p>}
            </div>
          )}

          {!isEditMode && kontekst?.maPrzełożonego && kontekst.glownyNaUrlopie && (
            <div className="mb-4 space-y-3">
              <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                <Palmtree className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  Twój przełożony ({kontekst.glownyPrzełożony?.firstName} {kontekst.glownyPrzełożony?.lastName}) jest na urlopie.
                  Wybierz członka zarządu, który rozpatrzy wniosek.
                </p>
              </div>
              {(kontekst.listaZarzadu || []).length === 0 ? (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
                  Brak dostępnych członków zarządu. Skontaktuj się z HR.
                </div>
              ) : (
                <select required value={formData.przełożony} onChange={set('przełożony')} className="input max-w-lg">
                  <option value="">— Wybierz —</option>
                  {kontekst.listaZarzadu.map((z) => (
                    <option key={z._id} value={z._id}>
                      {z.firstName && z.lastName ? `${z.firstName} ${z.lastName}` : z.username}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="mb-5 flex items-start gap-2 rounded-xl bg-primary-50/70 border border-primary-100 px-4 py-3 text-sm text-ink">
            <Info className="h-4 w-4 text-primary-600 shrink-0 mt-0.5" />
            <span>
              Po złożeniu wniosek trafi do przełożonego do decyzji, a członkowie zarządu otrzymają informację do wiadomości.
              Po wykonaniu nadgodzin potwierdź w systemie faktycznie przepracowany czas.
            </span>
          </div>

          <p className="text-sm font-medium text-ink mb-2">Czytelny podpis pracownika *</p>
          <SignaturePad
            variant="compact"
            onSignatureChange={(podpis) => setFormData((prev) => ({ ...prev, podpisPracownika: podpis }))}
            width={360}
            height={130}
            signature={formData.podpisPracownika || undefined}
            signerName={formatSignerNameUnderPad(formData.podpisPracownika, undefined, user)}
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          <Link to="/nadgodziny" className="btn-secondary">Anuluj</Link>
          <button
            type="submit"
            disabled={zapiszMutation.isLoading || brakAkceptanta || limitPrzekroczony || (!isEditMode && (kontekstLoading || kontekstError))}
            className="btn-primary flex items-center gap-2"
          >
            {zapiszMutation.isLoading ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
            <span>{isEditMode ? 'Wyślij poprawiony wniosek' : 'Złóż wniosek'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default WniosekNadgodziny;
